import { AttachmentFile, useModal } from '@billy/design-system'
import { trackError } from '@billy/track-error'
import isEmpty from 'lodash/isEmpty'
import React, {
  createContext,
  ReactElement,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { useTranslation } from 'react-i18next'
import { useAsync, usePrevious } from 'react-use'

import { useFetchAttachment } from '@features/attachments/hooks/useFetchAttachment'
import { BillState } from '@features/bills/enums/billState'
import { BillType } from '@features/bills/enums/billType'
import { useGetBill } from '@features/bills/hooks/useGetBill'
import { Bill } from '@features/bills/types/bill'
import { EInvoiceStatus } from '@features/eInvoicing/constants/eInvoiceStatus'
import { getEInvoiceLifecycleFromDataJson } from '@features/eInvoicing/utils/getEInvoiceLifecycleFromDataJson'
import { useAccounts } from '@modules-deprecated/app/accounts'
import { useUserOrganization } from '@modules-deprecated/app/organization'
import { useTaxRates } from '@modules-deprecated/app/taxrates'

import { useDirtyRoute } from '../../../../../contexts/dirtyRouteContext'
import {
  BillFormFieldsChangeSource,
  BillFormFieldsChangeSourceMap,
} from '../../../../../enums/billFormFieldsChangeSource'
import { BillFormView } from '../../../../../enums/billFormView'
import { ModalId } from '../../../../../enums/modalId'
import { useForm } from '../../../../../hooks/useForm'
import { APIError, getCurrentLocale } from '../../../../../utils'
import { IS_DEVELOPMENT } from '../../../../../utils/isDevelopment'
import { useFetchContact } from '../../../../contacts/hooks/useFetchContact'
import { useBillAttachmentPageContext } from '../../../contexts/billAttachmentPageContext'
import { BillSubmitAction } from '../../../enums/billSubmitAction'
import { BulkMode } from '../../../enums/bulkMode'
import { getAutoCompletedFormWithAttachmentData } from '../hooks/useAttachmentDataAutoComplete'
import { useBillMutations } from '../hooks/useBillMutations'
import { autoCompleteBillForm } from '../utils/autoCompleteBillForm'
import { convertBillFormToBillPayload } from '../utils/convertBillFormToBillPayload'
import type { BillForm, BillFormPartial } from '../utils/formData'
import { getDefaultBillLine, getDefaultValues, getValidationSchema } from '../utils/formData'
import { getAutoCompletedFormWithVendor } from '../utils/getAutoCompletedFormWithVendor'
import { getInitialBillForm } from '../utils/getInitialBillForm'

interface ContextState {
  approve: () => void
  attachmentId?: string
  billId?: string
  billFormView: BillFormView
  bulkEditMode?: BulkMode
  eInvoiceId: string | undefined
  eInvoiceReasonCode: string | null
  eInvoiceStatus: EInvoiceStatus | null
  isCreditNote?: boolean
  isCreditNoteFromBill?: boolean
  isDuplicate: boolean
  isFetching: boolean
  isLifecycleLocked: boolean
  isLifecycleStateOverridden: boolean
  isNewCreditNote?: boolean
  isSyncedToCPA: boolean
  overrideLifecycleState: () => void
  onOpenDuplicate?: () => void
  processingAction?: BillSubmitAction
  save: () => void
  setIsReferenceNumberError?: (isError: boolean) => void
  formFieldsChangeSource: BillFormFieldsChangeSourceMap
  setFormFieldsChangeSource: React.Dispatch<React.SetStateAction<BillFormFieldsChangeSourceMap>>
  modalDisplayMode?: boolean
}

const BillFormContext = createContext<ContextState | undefined>(undefined)

interface BillFormContextProps {
  attachmentId?: string
  billId?: string
  billIdToDuplicate?: string
  billIdToCreateCreditNote?: string
  children?: ReactNode
  contactId?: string
  initialFormValues?: BillFormPartial
  isBulkEditMode?: boolean
  isNewCreditNote?: boolean
  onOpenDuplicate?: () => void
  modalDisplayMode?: boolean
}

export const BillFormContextProvider = ({
  attachmentId,
  billId,
  billIdToCreateCreditNote,
  billIdToDuplicate,
  children,
  contactId,
  initialFormValues,
  isBulkEditMode,
  isNewCreditNote,
  onOpenDuplicate,
  modalDisplayMode,
}: BillFormContextProps): ReactElement => {
  const billIdToFetch = billId || billIdToDuplicate || billIdToCreateCreditNote
  const [isFetching, setIsFetching] = useState(!!billIdToFetch || !!attachmentId || !!contactId || !!initialFormValues)
  const [isReferenceNumberError, setIsReferenceNumberError] = useState(false)
  const [hasAttachmentBeenSet, setHasAttachmentBeenSet] = useState(false)
  const [formFieldsChangeSource, setFormFieldsChangeSource] = useState<BillFormFieldsChangeSourceMap>({
    main: BillFormFieldsChangeSource.Search,
  })
  const { t } = useTranslation()
  const { organization } = useUserOrganization()
  const locale = getCurrentLocale()
  const { setDirty } = useDirtyRoute()
  const { accounts = [], isLoading: isAccountsLoading } = useAccounts()
  const { taxRates = [], isLoading: isTaxRatesLoading } = useTaxRates()
  const previousBillId = usePrevious(billId)
  const previousAttachmentId = usePrevious(attachmentId)
  const { open: openRegisterPaymentModal } = useModal(ModalId.BillRegisterPaymentModal)
  const [, setCurrentPageIndex] = useBillAttachmentPageContext()
  const billFormView = billId ? BillFormView.edit : BillFormView.create

  // Bill fetch

  const { data: billData, isError: isFetchBillError } = useGetBill(billIdToFetch)
  const { attachment, isError: isFetchReceiptError, isLoading: isReceiptLoading } = useFetchAttachment(attachmentId)
  const { contact, isError: isFetchContactError, isLoading: isContactLoading } = useFetchContact(contactId)

  // Computed values

  const bulkEditMode = useMemo(() => {
    if (!isBulkEditMode) {
      return
    }

    if (billId) {
      return BulkMode.Bill
    }

    if (attachmentId) {
      return BulkMode.Attachment
    }
  }, [attachmentId, billId, isBulkEditMode])
  const isCreditNote = Boolean(billId && billData?.bill.type === BillType.CreditNote)
  const isCreditNoteFromBill = Boolean(billIdToCreateCreditNote)
  const isDuplicate = !!attachment?.file?.parentDuplicateFileId
  const isSyncedToCPA = !!billData?.bill?.integrationStatus?.length
  const attachmentDataJson = attachment?.file?.dataJson ?? billData?.bill.attachments[0]?.file?.dataJson
  const eInvoiceLifecycle = useMemo(() => getEInvoiceLifecycleFromDataJson(attachmentDataJson), [attachmentDataJson])
  const eInvoiceStatus = eInvoiceLifecycle?.eInvoiceStatus ?? null
  const eInvoiceReasonCode = eInvoiceLifecycle?.reasonCode ?? null
  const isDisputed = eInvoiceStatus === EInvoiceStatus.Disputed
  const isSuspended = eInvoiceStatus === EInvoiceStatus.Suspended

  const [isLifecycleStateOverridden, setIsLifecycleStateOverridden] = useState(false)
  const overrideLifecycleState = useCallback(() => setIsLifecycleStateOverridden(true), [])
  const isLifecycleLocked = (isDisputed || isSuspended) && !isLifecycleStateOverridden

  // Form initialization

  const {
    handleSubmit,
    setValue,
    Form,
    reset,
    formState: { isDirty, errors },
    getValues,
    setError,
  } = useForm({
    defaultValues: useMemo(() => getDefaultValues(organization?.baseCurrencyId), [organization?.baseCurrencyId]),
    validationSchema: useMemo(() => getValidationSchema(t, isReferenceNumberError), [t, isReferenceNumberError]),
  })

  // Custom error handler

  const errorsCustomHandler = useCallback(
    (error: APIError | undefined): boolean => {
      const propertiesErrors = error?.validationErrors?.bill?.attributes as Record<keyof Bill, string> | undefined

      if (propertiesErrors && propertiesErrors.paymentAccountId) {
        setError('paymentAccount', {
          type: 'custom',
          message: propertiesErrors.paymentAccountId,
        })

        openRegisterPaymentModal()
        setDirty(true)
        return true
      }

      return false
    },
    [openRegisterPaymentModal, setDirty, setError],
  )

  // Bill mutations

  const { createBill, updateBill, processingAction } = useBillMutations({
    bulkEditMode,
    billIdToCreateCreditNote,
    handleErrorManually: errorsCustomHandler,
  })

  // Executors

  const approve = useCallback(
    async (bill: BillForm) => {
      if (!bill.submitAction) {
        trackError('Submit action has to be set!')
        return
      }

      if (!organization) {
        return
      }

      setDirty(false)

      const saveAsCreditNote = isCreditNote || isNewCreditNote || isCreditNoteFromBill
      const billPayload = convertBillFormToBillPayload(bill, organization.id, saveAsCreditNote, BillState.Approved)

      if (billId) {
        updateBill({ billId, payload: { ...billPayload } }, bill.submitAction, bill.attachmentsFiles, bill.vendor)
      } else {
        createBill(billPayload, bill.submitAction, bill.attachmentsFiles, bill.vendor)
      }
    },

    [billId, createBill, isCreditNote, isCreditNoteFromBill, isNewCreditNote, organization, setDirty, updateBill],
  )

  const save = useCallback(
    (bill: BillForm) => {
      if (!bill.submitAction) {
        trackError('Submit action has to be set!')
        return
      }

      if (!organization) {
        return
      }

      setDirty(false)

      const saveAsCreditNote = isCreditNote || isNewCreditNote || isCreditNoteFromBill
      const billPayload = convertBillFormToBillPayload(bill, organization.id, saveAsCreditNote)

      if (billId) {
        updateBill({ billId, payload: billPayload }, bill.submitAction, bill.attachmentsFiles, bill.vendor)
      } else {
        createBill(billPayload, bill.submitAction, bill.attachmentsFiles, bill.vendor)
      }
    },
    [billId, createBill, isCreditNote, isCreditNoteFromBill, isNewCreditNote, organization, setDirty, updateBill],
  )

  // Resetting attachments page index to reset preview to first attachment when bill changes in bulk edit mode

  const resetAttachmentsPageIndex = useCallback(() => {
    setCurrentPageIndex(0)
  }, [setCurrentPageIndex])

  // Handlers

  const handleApprove = useCallback(() => {
    if (modalDisplayMode) {
      setValue('submitAction', BillSubmitAction.ApproveAndClose)
    } else if (bulkEditMode) {
      setValue('submitAction', BillSubmitAction.ApproveBulk)
    } else {
      setValue('submitAction', BillSubmitAction.Approve)
    }

    handleSubmit(approve)()
  }, [approve, bulkEditMode, handleSubmit, setValue, modalDisplayMode])

  const handleSave = useCallback(() => {
    setValue('submitAction', BillSubmitAction.Save) // helper form value for validation check and correct processing after mutation
    handleSubmit(save)()
  }, [handleSubmit, save, setValue])

  // Observe initial values provided by an entry point, such as a selected bank transaction
  useAsync(async () => {
    if (isTaxRatesLoading || isAccountsLoading || !organization?.id || !initialFormValues) {
      return
    }

    let billFormUpdated = autoCompleteBillForm({
      baseCurrencyId: organization.baseCurrencyId,
      billFormDefault: getDefaultValues(organization.baseCurrencyId),
      billFormOverrides: initialFormValues,
      onlyFirstBillLine: false,
    })

    if (billFormUpdated.vendor) {
      billFormUpdated = await getAutoCompletedFormWithVendor({
        accounts,
        baseCurrencyId: organization.baseCurrencyId,
        billForm: billFormUpdated,
        locale,
        organizationCountryCode: organization.countryId || 'DK',
        organizationId: organization.id,
        taxRates,
        vendor: billFormUpdated.vendor,
      })
    }

    reset(billFormUpdated)

    window.requestAnimationFrame(() => {
      setValue('autoCompletedFields', billFormUpdated.autoCompletedFields)
    })

    setIsFetching(false)
  }, [initialFormValues, isTaxRatesLoading, isAccountsLoading, organization?.id])

  // Observe bill
  useAsync(async () => {
    if (isTaxRatesLoading || isAccountsLoading || !organization?.id || !billData || processingAction) {
      return
    }

    const billForm = await getInitialBillForm({
      accounts,
      billData,
      isDuplicate: !!billIdToDuplicate,
      isCreditNoteFromBill: !!billIdToCreateCreditNote,
      locale,
      organization,
      billFormCurrent: {
        submitAction: getValues('submitAction'),
        bookkeepingTagSuggestions: getValues('bookkeepingTagSuggestions'),
      },
      t,
      taxRates,
    })

    if (billForm) {
      resetAttachmentsPageIndex()
      reset(billForm)

      // Wait one frame before setting it to false to better handle loading states -> form needs it to correctly update state
      window.requestAnimationFrame(() => {
        setValue('autoCompletedFields', billForm.autoCompletedFields)
      })

      setIsFetching(false)
    }
  }, [billData, isTaxRatesLoading, isAccountsLoading, processingAction])

  // Observe attachment
  useAsync(async () => {
    if (isTaxRatesLoading || isAccountsLoading || isReceiptLoading || !organization?.id) {
      return
    }

    if (attachment?.file) {
      if (hasAttachmentBeenSet) {
        setIsFetching(false)
        return
      }

      const attachmentFile: AttachmentFile | undefined = {
        ...attachment.file,
        attachmentId: attachment.id,
        attachmentType: attachment.type,
      }

      // we treat it as "chosen" and this attachment is kind of chosen from the receipts, and not assigned to the bill
      // we also don't want to create a new attachment, and "attachmentsFilesChosen" are passed as attachmentsIds in the payload
      const billForm: BillForm = {
        ...getDefaultValues(organization?.baseCurrencyId),
        attachmentsFiles: [attachmentFile],
        attachmentsFilesChosen: [attachmentFile],
      }

      const billFormUpdated = await getAutoCompletedFormWithAttachmentData({
        accounts,
        attachmentFile,
        baseCurrencyId: organization?.baseCurrencyId,
        billForm,
        chartOfAccountId: organization.chartOfAccountId,
        countryId: organization?.countryId,
        locale,
        organizationId: organization?.id,
        taxRates,
      })

      if (attachment.comment) {
        if (!billFormUpdated.billLines?.length) {
          billFormUpdated.billLines = [
            {
              ...getDefaultBillLine(),
              description: attachment.comment,
            },
          ]
        } else {
          billFormUpdated.billLines[0].description = attachment.comment
        }

        billFormUpdated.autoCompletedFields = {
          ...billFormUpdated.autoCompletedFields,
          'billLines.0.description': true,
        }
      }

      reset(billFormUpdated)

      // Wait one frame before setting autoCompletedFields
      window.requestAnimationFrame(() => {
        setValue('autoCompletedFields', billFormUpdated.autoCompletedFields)
      })

      setIsFetching(false)
      setHasAttachmentBeenSet(true)
    }
  }, [
    attachment?.file,
    attachment?.id,
    isTaxRatesLoading,
    isAccountsLoading,
    isReceiptLoading,
    hasAttachmentBeenSet,
    organization?.id,
  ])

  // Observe contact
  useAsync(async () => {
    if (isTaxRatesLoading || isAccountsLoading || isContactLoading || !organization?.id) {
      return
    }

    if (contact) {
      setValue('vendor', contact)

      const billFormUpdated = await getAutoCompletedFormWithVendor({
        accounts,
        baseCurrencyId: organization?.baseCurrencyId,
        billForm: getValues(),
        locale,
        organizationId: organization?.id,
        organizationCountryCode: organization.countryId || 'DK',
        taxRates,
        vendor: contact,
      })

      billFormUpdated.autoCompletedFields = {
        ...billFormUpdated.autoCompletedFields,
        vendor: true,
      }

      reset(billFormUpdated)

      // Wait one frame before setting autoCompletedFields
      window.requestAnimationFrame(() => {
        setValue('autoCompletedFields', billFormUpdated.autoCompletedFields)
      })

      setIsFetching(false)
    }
  }, [contact?.id, isTaxRatesLoading, isAccountsLoading, isContactLoading, organization?.id])

  useEffect(() => {
    if (isFetchReceiptError || isFetchBillError || isFetchContactError) {
      setIsFetching(false)
    }
  }, [isFetchBillError, isFetchReceiptError, isFetchContactError])

  useEffect(() => {
    if ((billId && !billData) || (attachmentId && !attachment) || (contactId && !contact)) {
      setIsFetching(true)
    }

    if (
      (previousBillId && previousBillId !== billId) ||
      (previousAttachmentId && previousAttachmentId !== attachmentId)
    ) {
      if (billId || attachmentId) {
        setIsFetching(true)
      }

      setHasAttachmentBeenSet(false)
      resetAttachmentsPageIndex()
      reset(getDefaultValues(organization?.baseCurrencyId))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [billId, attachmentId, contactId])

  useEffect(() => {
    if (organization?.baseCurrencyId && !getValues('currencyId')) {
      setValue('currencyId', organization.baseCurrencyId)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organization?.baseCurrencyId])

  // @todo remove it from here as it causes re-renders
  if (!isEmpty(errors) && IS_DEVELOPMENT) {
    // Some errors might not be visible in the UI (if not handled) so just in case we console them
    trackError(errors)
  }

  // Clear lifecycle override on navigation or e-invoice status change so the banner reappears
  useEffect(() => {
    setIsLifecycleStateOverridden(false)
  }, [billId, attachmentId, eInvoiceStatus])

  useEffect(() => {
    setDirty(isDirty)
    // Run effect only on `isDirty` value change
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDirty])

  return (
    <BillFormContext.Provider
      value={{
        approve: handleApprove,
        attachmentId,
        billFormView,
        billId,
        bulkEditMode,
        eInvoiceId: attachment?.eInvoiceId,
        eInvoiceReasonCode,
        eInvoiceStatus,
        isCreditNote,
        isFetching,
        isLifecycleLocked,
        isLifecycleStateOverridden,
        isNewCreditNote,
        isCreditNoteFromBill,
        isDuplicate,
        isSyncedToCPA,
        overrideLifecycleState,
        onOpenDuplicate,
        processingAction,
        save: handleSave,
        setIsReferenceNumberError,
        formFieldsChangeSource,
        setFormFieldsChangeSource,
        modalDisplayMode,
      }}
    >
      <Form>{children}</Form>
    </BillFormContext.Provider>
  )
}

export const useBillForm = () => {
  const context = useContext(BillFormContext)

  if (!context) {
    throw new Error('BillFormContextProvider is missing in the module!')
  }

  return context
}
